import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { CircularSealBadge } from "@/components/CircularSealBadge";
import { SubScorePill } from "./SubScorePill";
import { LimitationPills } from "@/components/browse/LimitationPills";


import type { BlogCafeEntry } from "@/hooks/useBlogPage";


interface Props {
  rank: number;
  entry: BlogCafeEntry;
}

export function RankedCafeCard({ rank, entry }: Props) {
  const { cafe, breakdown, description } = entry;
  const href = cafe.slug ? `/cafe/${cafe.slug}` : undefined;

  return (
    <article className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      {/* Hero image */}
      {cafe.cover_image_url ? (
        <Link to={href ?? "#"} className="block">
          <div className="aspect-[16/9] w-full overflow-hidden bg-muted">
            <img
              src={cafe.cover_image_url}
              alt={`${cafe.name} interior`}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
            />
          </div>
        </Link>
      ) : (
        <div className="aspect-[16/9] w-full bg-muted" />
      )}

      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))] font-serif text-lg font-bold text-[hsl(var(--primary-foreground))] sm:h-12 sm:w-12 sm:text-xl">
            {rank}
          </div>

          <div className="flex-1 min-w-0">
            {href ? (
              <Link to={href} className="hover:underline">
                <h3 className="font-serif text-2xl font-semibold leading-tight text-foreground sm:text-3xl">
                  {cafe.name}
                </h3>
              </Link>
            ) : (
              <h3 className="font-serif text-2xl font-semibold leading-tight text-foreground sm:text-3xl">
                {cafe.name}
              </h3>
            )}
            <p className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{cafe.address}</span>
            </p>
          </div>

          <div className="flex flex-col items-center gap-1">
            <CircularSealBadge score={cafe.work_readiness_score ?? 0} size="default" />
          </div>

        </div>

        {/* Sub-score pills */}
        <div className="mt-5 flex flex-wrap gap-2">
          <SubScorePill type="wifi" {...breakdown.wifi} />
          <SubScorePill type="outlets" {...breakdown.outlets} />
          <SubScorePill type="seating" {...breakdown.seating} />
          <SubScorePill type="parking" {...breakdown.parking} />
          <LimitationPills categories={cafe.work_note_categories} />
        </div>


        {/* Written description */}
        {description && (
          <div className="mt-5 space-y-3 text-[15px] leading-relaxed text-foreground/85">
            {description.split(/\n\n+/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
